import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Crown, Sparkles } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AuthApiError,
  fetchCurrentSession,
  fetchMembershipSummary,
} from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { buildDeviceFingerprint } from '@/features/auth/utils/device-fingerprint'
import {
  createBillingOrder,
  fetchBillingOptions,
  fetchBillingOrderStatus,
} from '@/features/billing/services/billing-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  formatAiHoursLabel,
  formatBillingCycleLabel,
  formatBillingPriceLabel,
  formatBillingSecondaryPrice,
  formatMembershipExpiresAt,
  formatMembershipPlan,
  formatMembershipStatus,
  formatRemainingAiHoursLabel,
  getPlanAiProcessingSeconds,
  hasSubscriptionMembership,
} from '@/features/billing/utils/membership-labels'
import type {
  BillingChannelAvailability,
  BillingOrderResult,
  BillingPageContent,
  BillingPlan,
} from '@/features/billing/types'

const fallbackContent: BillingPageContent = {
  badgeText: '会员定价',
  title: '简单订阅，不做复杂套餐',
  description: '按月获得 AI 处理时长，用多少看得清，升级也更容易理解。',
}

const fallbackAvailability: BillingChannelAvailability = {
  alipay: true,
  wechat: true,
  notifyBaseUrl: '',
  alipayMessage: '支付宝可用',
  wechatMessage: '微信支付可用',
}

const MEMBERSHIP_DIALOG_DESCRIPTION = '手动剪辑免费，AI 处理按月提供时长，自动化能力随套餐开放。'

function pickDefaultPlan(plans: BillingPlan[], currentPlanCode: string) {
  return (
    plans.find((plan) => plan.plan_code !== 'fastcut_free' && plan.plan_code !== currentPlanCode) ||
    plans.find((plan) => plan.plan_code !== 'fastcut_free') ||
    plans[0] ||
    null
  )
}

function isMobileDevice() {
  if (typeof window === 'undefined') {
    return false
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|HarmonyOS/i.test(
    window.navigator.userAgent,
  )
}

function isFreePlan(plan: BillingPlan | null | undefined) {
  return !plan || plan.plan_code === 'fastcut_free'
}

function shouldDisplayPlan(plan: BillingPlan) {
  if (plan.plan_code === 'fastcut_free') {
    return true
  }

  return plan.billing_cycle !== 'yearly'
}

export function MembershipPurchaseDialog() {
  const open = useMembershipDialogStore((state) => state.open)
  const closeDialog = useMembershipDialogStore((state) => state.closeDialog)
  const session = useAuthSessionStore((state) => state.session)
  const setSession = useAuthSessionStore((state) => state.setSession)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [availability, setAvailability] = useState<BillingChannelAvailability>(fallbackAvailability)
  const [content, setContent] = useState<BillingPageContent>(fallbackContent)
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null)
  const [activeOrder, setActiveOrder] = useState<BillingOrderResult | null>(null)
  const [activeOrderPlanCode, setActiveOrderPlanCode] = useState<string | null>(null)
  // 记录自动建单失败的套餐，避免 effect 在失败后反复触发导致请求风暴（例如登录失效时连续 401）。
  const [failedOrderPlanCode, setFailedOrderPlanCode] = useState<string | null>(null)
  const [loadingProvider, setLoadingProvider] = useState<'alipay' | 'wechat' | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<'alipay' | 'wechat'>('alipay')
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [error, setError] = useState('')
  const [paymentHint, setPaymentHint] = useState('支付完成后，会员状态会自动刷新。')
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [deviceFingerprint, setDeviceFingerprint] = useState('')
  const mobileWeb = useMemo(() => isMobileDevice(), [])
  const visiblePlans = useMemo(
    () => plans.filter(shouldDisplayPlan),
    [plans],
  )

  const currentPlanCode = session?.membership?.planCode || 'fastcut_free'
  const selectedPlan =
    visiblePlans.find((plan) => plan.plan_code === selectedPlanCode) ||
    pickDefaultPlan(visiblePlans, currentPlanCode)
  const currentPlanLabel = formatMembershipPlan(session?.membership?.planName, session?.membership?.planCode)
  const currentStatusLabel = formatMembershipStatus(session?.membership?.status)
  const currentExpiresAtLabel = formatMembershipExpiresAt(session?.membership?.expiresAt, '')
  const showCurrentExpiresAt = hasSubscriptionMembership(
    session?.membership?.planCode,
    session?.membership?.expiresAt,
  )
  const currentPlanAiSeconds = getPlanAiProcessingSeconds(session?.membership?.planCode ?? currentPlanCode)
  const currentAiSeconds = session?.membership?.quotas?.aiProcessingSecondsRemaining ?? null

  const resetLocalState = useCallback(() => {
    setSelectedProvider('alipay')
    setActiveOrder(null)
    setActiveOrderPlanCode(null)
    setFailedOrderPlanCode(null)
    setPaymentSuccess(false)
    setPaymentHint('支付完成后，会员状态会自动刷新。')
    setError('')
  }, [])

  useEffect(() => {
    void buildDeviceFingerprint().then(setDeviceFingerprint)
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    setLoadingOptions(true)
    setError('')
    void fetchMembershipSummary()
      .then((membership) => {
        if (membership) {
          updateMembership(membership)
        }
      })
      .catch(() => {
        // Keep the dialog usable even if the latest membership summary fails to refresh.
      })

    void fetchBillingOptions()
      .then((payload) => {
        setPlans(payload.plans)
        setAvailability(payload.availability)
        setContent(payload.content)
        const displayPlans = payload.plans.filter(shouldDisplayPlan)
        setSelectedPlanCode(
          (current) =>
            current ||
            pickDefaultPlan(displayPlans, currentPlanCode)?.plan_code ||
            null,
        )
      })
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : '加载会员套餐失败')
      })
      .finally(() => {
        setLoadingOptions(false)
      })
  }, [currentPlanCode, open, updateMembership])

  useEffect(() => {
    if (!open || !activeOrder?.orderNo || paymentSuccess) {
      return
    }

    let stopped = false
    const timer = window.setInterval(async () => {
      try {
        const payload = await fetchBillingOrderStatus(activeOrder.orderNo)
        if (stopped) {
          return
        }

        if (payload.order?.status === 'paid') {
          setPaymentSuccess(true)
          setPaymentHint('支付成功，会员状态已更新。')

          const latestSession = await fetchCurrentSession()
          if (latestSession) {
            setSession(latestSession)
          }

          const membership = await fetchMembershipSummary()
          if (membership) {
            updateMembership(membership)
          }

          toast.success('会员已开通')
          window.clearInterval(timer)
          resetLocalState()
          closeDialog()
        }
      } catch {
        if (!stopped) {
          setPaymentHint('支付状态刷新失败，请支付完成后稍后再试。')
        }
      }
    }, 3000)

    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [activeOrder?.orderNo, closeDialog, open, paymentSuccess, resetLocalState, setSession, updateMembership])

  const resetDialogState = (nextOpen: boolean) => {
    if (nextOpen) {
      return
    }

    resetLocalState()
    closeDialog()
  }

  const handleCreateOrder = useCallback(async (provider: 'alipay' | 'wechat') => {
    if (!selectedPlan) {
      setError('没有可用的会员套餐')
      return
    }

    setSelectedProvider(provider)
    setLoadingProvider(provider)
    setError('')
    setPaymentSuccess(false)

    try {
      const order = await createBillingOrder({
        planCode: selectedPlan.plan_code,
        provider,
        deviceFingerprint,
        source: 'subsite-videocut',
      })

      setActiveOrder(order)
      setActiveOrderPlanCode(selectedPlan.plan_code)
      setFailedOrderPlanCode(null)
      setPaymentHint(
        order.paymentMode === 'redirect'
          ? '正在跳转到支付宝支付页面。'
          : provider === 'wechat' && mobileWeb
            ? '微信支付仍需扫码完成，请使用另一台设备识别二维码。'
            : '二维码已生成，请扫码完成支付。',
      )

      if (order.paymentMode === 'redirect' && order.paymentUrl) {
        window.setTimeout(() => {
          window.open(order.paymentUrl, '_blank', 'noopener,noreferrer')
        }, 120)
      }
    } catch (requestError) {
      // 标记当前套餐建单失败，阻止自动建单 effect 立即重试（否则会刷一长串请求）。
      setFailedOrderPlanCode(selectedPlan.plan_code)

      const status = requestError instanceof AuthApiError ? requestError.status : undefined
      if (status === 401) {
        setError('登录状态已失效，请重新登录后再购买会员。')
      } else {
        setError(requestError instanceof Error ? requestError.message : '创建订单失败')
      }
    } finally {
      setLoadingProvider(null)
    }
  }, [deviceFingerprint, mobileWeb, selectedPlan])

  useEffect(() => {
    if (!open) {
      return
    }

    if (isFreePlan(selectedPlan)) {
      setActiveOrder(null)
      setActiveOrderPlanCode(null)
      return
    }

    if (activeOrderPlanCode && activeOrderPlanCode !== selectedPlan?.plan_code) {
      setActiveOrder(null)
      setActiveOrderPlanCode(null)
      setPaymentSuccess(false)
      setPaymentHint('正在生成支付二维码，请稍候。')
      return
    }

    // 上一次为当前套餐自动建单失败时，不再自动重试，等待用户手动点击支付按钮重试。
    if (failedOrderPlanCode === selectedPlan?.plan_code) {
      return
    }

    if (
      loadingOptions ||
      loadingProvider !== null ||
      activeOrder ||
      activeOrderPlanCode === selectedPlan?.plan_code
    ) {
      return
    }

    void handleCreateOrder('alipay')
  }, [
    activeOrder,
    activeOrderPlanCode,
    failedOrderPlanCode,
    handleCreateOrder,
    loadingOptions,
    loadingProvider,
    open,
    selectedPlan,
  ])

  return (
    <Dialog open={open} onOpenChange={resetDialogState}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden border-border bg-card p-0 sm:rounded-3xl">
        <div className="grid max-h-[90vh] gap-0 md:grid-cols-[1.05fr_0.95fr]">
          <div className="overflow-y-auto border-b border-border p-6 md:border-b-0 md:border-r md:p-8">
            <DialogHeader>
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <Crown className="h-3.5 w-3.5" />
                {content.badgeText}
              </div>
              <DialogTitle className="mt-4 text-3xl font-semibold tracking-tight">
                {content.title}
              </DialogTitle>
              {MEMBERSHIP_DIALOG_DESCRIPTION ? (
                <DialogDescription className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                  {MEMBERSHIP_DIALOG_DESCRIPTION}
                </DialogDescription>
              ) : null}
            </DialogHeader>

            <div className="mt-6 space-y-3">
              {loadingOptions ? (
                <div className="rounded-2xl border border-border bg-background/60 p-4 text-sm text-muted-foreground">
                  正在加载会员套餐...
                </div>
              ) : null}

              {visiblePlans.map((plan) => {
                const isCurrent = currentPlanCode === plan.plan_code
                const isSelected = selectedPlan?.plan_code === plan.plan_code
                const aiSeconds = getPlanAiProcessingSeconds(plan.plan_code)
                const highlights = plan.feature_flags.slice(0, 4)

                return (
                  <button
                    key={plan.plan_code}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setSelectedPlanCode(plan.plan_code)}
                    className={`w-full rounded-3xl border p-5 text-left transition ${
                      isCurrent
                        ? 'border-primary/40 bg-primary/10 opacity-85'
                        : isSelected
                          ? 'border-primary bg-background shadow-sm'
                          : 'border-border bg-background/60 hover:border-primary/60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-lg font-semibold">{plan.plan_name}</div>
                          {plan.plan_code === 'fastcut_studio_month' ? (
                            <span className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
                              推荐
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {plan.description || '适合当前剪辑与 AI 创作场景'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-semibold">{formatBillingPriceLabel(plan)}</div>
                        {plan.original_price ? (
                          <div className="text-xs text-muted-foreground line-through">
                            {plan.original_price} 元
                          </div>
                        ) : null}
                        <div className="mt-1 text-xs text-muted-foreground">
                          {formatBillingCycleLabel(plan)}
                        </div>
                        {isCurrent ? (
                          <div className="mt-2 text-xs font-semibold text-primary">当前套餐</div>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 rounded-2xl bg-background/80 px-3 py-3 text-sm">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span className="font-medium">{formatAiHoursLabel(aiSeconds)}</span>
                    </div>

                    {formatBillingSecondaryPrice(plan) ? (
                      <div className="mt-2 text-xs text-muted-foreground">
                        {formatBillingSecondaryPrice(plan)}
                      </div>
                    ) : null}

                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      {highlights.map((feature) => (
                        <div key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="mt-0.5 h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="overflow-y-auto p-6 md:p-8">
            <div className="rounded-3xl border border-border bg-background/60 p-5">
              <div className="text-sm font-medium text-muted-foreground">当前选择</div>
              <div className="mt-2 text-2xl font-semibold">
                {selectedPlan?.plan_name || '请选择套餐'}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {selectedPlan
                  ? `${formatBillingPriceLabel(selectedPlan)} · ${formatAiHoursLabel(
                      getPlanAiProcessingSeconds(selectedPlan.plan_code),
                    )}`
                  : '请选择要开通的会员套餐'}
              </div>

              <div className="mt-4 rounded-2xl border border-border/80 bg-card/80 px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/80">
                      当前会员
                    </div>
                    <div className="mt-1 text-base font-semibold text-foreground">{currentPlanLabel}</div>
                  </div>
                  <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    {currentStatusLabel}
                  </div>
                </div>
                <div className="mt-3 grid gap-2 border-t border-border/70 pt-3 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between gap-3">
                    <span>套餐包含</span>
                    <span className="font-medium text-foreground">
                      {formatAiHoursLabel(currentPlanAiSeconds)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>当前剩余</span>
                    <span className="font-medium text-foreground">
                      {formatRemainingAiHoursLabel(currentAiSeconds)}
                    </span>
                  </div>
                </div>
                {showCurrentExpiresAt ? (
                  <div className="mt-2 text-sm text-muted-foreground">
                    有效期至 {currentExpiresAtLabel}
                  </div>
                ) : null}
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  disabled={isFreePlan(selectedPlan) || loadingProvider !== null || !availability.alipay}
                  variant={selectedProvider === 'alipay' ? 'default' : 'outline'}
                  onClick={() => handleCreateOrder('alipay')}
                  className="h-11"
                >
                  {loadingProvider === 'alipay' ? '生成中...' : mobileWeb ? '支付宝支付' : '支付宝扫码'}
                </Button>
                <Button
                  type="button"
                  disabled={isFreePlan(selectedPlan) || loadingProvider !== null || !availability.wechat}
                  variant={selectedProvider === 'wechat' ? 'default' : 'outline'}
                  onClick={() => handleCreateOrder('wechat')}
                  className="h-11"
                >
                  {loadingProvider === 'wechat' ? '生成中...' : '微信扫码'}
                </Button>
              </div>

              {error ? (
                <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              ) : null}

              <div className="mt-4 text-xs leading-6 text-muted-foreground">
                {selectedProvider === 'alipay'
                  ? availability.alipayMessage
                  : availability.wechatMessage || '支付完成后，会员状态会自动刷新。'}
              </div>
            </div>

            <div className="mt-5 min-h-[320px] rounded-3xl border border-border bg-background/60 p-5">
              {activeOrder?.paymentMode === 'qr' && activeOrder.qrCodeUrl ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <QRCodeSVG value={activeOrder.qrCodeUrl} size={220} level="M" includeMargin />
                  <div className="mt-4 text-lg font-semibold">
                    请使用{activeOrder.provider === 'alipay' ? '支付宝' : '微信'}扫码
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">订单号：{activeOrder.orderNo}</div>
                  <div
                    className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                      paymentSuccess ? 'bg-green-500/10 text-green-400' : 'bg-card text-muted-foreground'
                    }`}
                  >
                    {paymentHint}
                  </div>
                </div>
              ) : activeOrder?.paymentMode === 'redirect' && activeOrder.paymentUrl ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="text-lg font-semibold">已为你准备支付宝支付</div>
                  <div className="mt-3 text-sm leading-6 text-muted-foreground">
                    如果浏览器拦截弹窗，可以点击下方按钮继续支付。
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">订单号：{activeOrder.orderNo}</div>
                  <Button asChild className="mt-5">
                    <a href={activeOrder.paymentUrl} target="_blank" rel="noreferrer">
                      继续支付
                    </a>
                  </Button>
                  <div
                    className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                      paymentSuccess ? 'bg-green-500/10 text-green-400' : 'bg-card text-muted-foreground'
                    }`}
                  >
                    {paymentHint}
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="text-lg font-semibold">正在生成支付二维码</div>
                  <div className="mt-3 max-w-sm text-sm leading-6 text-muted-foreground">
                    首次打开会自动为你准备默认支付方式，请稍候。
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
